import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowClockwise, ArrowRight, CaretDown, CheckCircle, File, Flask, FlowArrow, PencilSimple, Plus, TextAa, Trash, User, WarningCircle,
} from '@phosphor-icons/react';
import {
  BestaetigenDialog, DialogHuelle, FeldRaster, ImmoButton, IosSwitch, LeerZustand, SchwebenderToast, TextFeld, useToastPill,
} from '../../../../components/immobilien/ui';
import { supabase } from '../../../../lib/supabase/client';
import { useMobilModus } from '../../../../lib/immobilien/mobil';
import { ABSENDER_TYP, absenderTypLabel } from '../../../../lib/dokumente/ablage';
import {
  formularZuRegel, pruefeRegel, regelZuFormular, testeRegeln, type AblageRegel, type RegelFormular, type RegelTestEingabe,
} from '../../../../lib/dokumente/ablageRegeln';

type Regel = AblageRegel & { name: string };

const fehlerText = (e: unknown, sonst: string) => (e instanceof Error && e.message ? e.message : sonst);
const MONO = "ui-monospace, 'SF Mono', Menlo, monospace";
const FELD_LABEL: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: 'var(--ink3)' };
const EINGABE: React.CSSProperties = { height: 44, padding: '0 14px', borderRadius: 14, border: 'none', fontSize: 14, fontWeight: 700, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--ink)', boxShadow: 'inset 0 0 0 1px var(--line)', width: '100%', minWidth: 0 };

function AbsenderAuswahl({ wert, onChange, label, alleLabel }: { wert: string; onChange: (v: string) => void; label: string; alleLabel: string }) {
  return (
    <label className="flex flex-col min-w-0" style={{ gap: 6 }}>
      <span style={FELD_LABEL}>{label}</span>
      <span className="relative flex items-center">
        <select value={wert} onChange={e => onChange(e.target.value)} style={{ ...EINGABE, appearance: 'none', paddingRight: 36, cursor: 'pointer' }}>
          <option value="">{alleLabel}</option>
          {Object.entries(ABSENDER_TYP).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <CaretDown weight="bold" size={11} color="var(--ink3)" style={{ position: 'absolute', right: 14, pointerEvents: 'none' }} />
      </span>
    </label>
  );
}

const Kriterium = ({ icon: I, children }: { icon: typeof User; children: React.ReactNode }) => (
  <span className="flex items-center" style={{ height: 26, padding: '0 9px', borderRadius: 13, gap: 5, fontSize: 12, fontWeight: 600, background: 'var(--fill2)', color: 'var(--ink2)', maxWidth: '100%' }}>
    <I weight="bold" size={12} style={{ flexShrink: 0 }} /><span className="truncate">{children}</span>
  </span>
);

/** Regel anlegen/bearbeiten. */
function RegelDialog({ start, pfadVorschlaege, onSchliessen, onGespeichert }: {
  start: Regel | null; pfadVorschlaege: readonly string[]; onSchliessen: () => void; onGespeichert: (text: string) => void;
}) {
  const [f, setF] = useState<RegelFormular>(() => (start ? regelZuFormular(start) : { ...regelZuFormular({}), prioritaet: '100' }));
  const [geprueft, setGeprueft] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const fe = pruefeRegel(f);
  const setze = (k: keyof RegelFormular) => (v: string) => setF(x => ({ ...x, [k]: v }));
  const speichern = async () => {
    setGeprueft(true);
    if (Object.keys(fe).length) return;
    setLaeuft(true); setFehler(null);
    const werte = { ...formularZuRegel(f), updated_at: new Date().toISOString() };
    const { error } = start
      ? await supabase.from('dokument_ablage_regeln').update(werte).eq('id', start.id)
      : await supabase.from('dokument_ablage_regeln').insert({ ...werte, aktiv: true });
    setLaeuft(false);
    if (error) { setFehler(error.message); return; }
    onGespeichert(start ? `Regel „${werte.name}“ gespeichert` : `Regel „${werte.name}“ angelegt`);
  };
  const zeige = (k: keyof RegelFormular) => (geprueft ? fe[k] : undefined);
  return (
    <DialogHuelle titel={start ? 'Regel bearbeiten' : 'Neue Regel'} icon={FlowArrow} onSchliessen={onSchliessen} laeuft={laeuft} breite={560}
      fuss={<>
        <ImmoButton art="fuellung" groesse={44} pille={false} onClick={onSchliessen} disabled={laeuft} style={{ flex: 1 }}>Abbrechen</ImmoButton>
        <ImmoButton art="akzent" groesse={44} pille={false} icon={CheckCircle} onClick={() => void speichern()} disabled={laeuft} style={{ flex: 1 }}>{laeuft ? 'Wird gespeichert …' : 'Speichern'}</ImmoButton>
      </>}>
      <FeldRaster minBreite={200}>
        <TextFeld label="Name" wert={f.name} onChange={setze('name')} kompakt fehler={zeige('name')} placeholder="z. B. Eingangsrechnung Dienstleister" />
        <TextFeld label="Priorität" wert={f.prioritaet} onChange={setze('prioritaet')} kompakt inputMode="numeric" fehler={zeige('prioritaet')} />
      </FeldRaster>
      <span style={{ fontSize: 11, color: 'var(--ink3)', marginTop: -6 }}>Kleinere Zahl wird zuerst geprüft.</span>
      <TextFeld label="Stichwörter (kommagetrennt)" wert={f.stichwoerter} onChange={setze('stichwoerter')} kompakt placeholder="rechnung, invoice" />
      <FeldRaster minBreite={200}>
        <AbsenderAuswahl label="Absender" wert={f.absender_typ} onChange={setze('absender_typ')} alleLabel="Jeder Absender" />
        <TextFeld label="Dateityp (kommagetrennt)" wert={f.dateityp} onChange={setze('dateityp')} kompakt placeholder="pdf" />
      </FeldRaster>
      <label className="flex flex-col" style={{ gap: 6 }}>
        <span style={FELD_LABEL}>Zielordner</span>
        <input value={f.ziel_pfad_vorlage} onChange={e => setze('ziel_pfad_vorlage')(e.target.value)} list="regel-zielordner" placeholder="02 Finanzen/Rechnungen/{Jahr}/Eingangsrechnungen"
          style={{ ...EINGABE, fontFamily: MONO, fontSize: 13, boxShadow: zeige('ziel_pfad_vorlage') ? 'inset 0 0 0 1.5px var(--bad)' : EINGABE.boxShadow }} />
        <datalist id="regel-zielordner">{pfadVorschlaege.map(p => <option key={p} value={p} />)}</datalist>
        <span style={{ fontSize: 11, color: zeige('ziel_pfad_vorlage') ? 'var(--bad)' : 'var(--ink3)' }}>{zeige('ziel_pfad_vorlage') ?? '{Jahr} wird beim Ablegen durch das aktuelle Jahr ersetzt.'}</span>
      </label>
      <TextFeld label="Dokument-Wort" wert={f.dokument_wort} onChange={setze('dokument_wort')} kompakt fehler={zeige('dokument_wort')} placeholder="Rechnung" />
      <span style={{ fontSize: 11, color: 'var(--ink3)', marginTop: -6 }}>Steht im Dateinamen nach Datum und Objektnummer.</span>
      {fehler && <span role="alert" style={{ fontSize: 12, fontWeight: 700, color: 'var(--bad)' }}>{fehler}</span>}
    </DialogHuelle>
  );
}

/** Ablage-Regeln (Handoff Dokumente §6.6, Screens 13/14): Liste nach Priorität, Anlegen/Bearbeiten, Aktiv, Löschen, Regel testen. */
export function RegelnTab() {
  const { mobil } = useMobilModus();
  const [toast, zeigeToast] = useToastPill(3200);
  const [regeln, setRegeln] = useState<Regel[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [runde, setRunde] = useState(0);
  const [dialog, setDialog] = useState<{ regel: Regel | null } | null>(null);
  const [loeschen, setLoeschen] = useState<Regel | null>(null);
  const [loescht, setLoescht] = useState(false);
  const [test, setTest] = useState<RegelTestEingabe>({ text: '', dateiname: '', absender_typ: '', dateityp: '', objektnummer: '' });
  const testAktiv = !!(test.text.trim() || test.dateiname.trim());

  useEffect(() => {
    let aktiv = true;
    supabase.from('dokument_ablage_regeln').select('id, aktiv, prioritaet, name, kategorie_id, stichwoerter, absender_typ, dateityp, ziel_pfad_vorlage, dokument_wort')
      .order('prioritaet').order('name')
      .then(({ data, error }) => {
        if (!aktiv) return;
        if (error) setFehler(error.message); else { setRegeln((data ?? []) as Regel[]); setFehler(null); }
      });
    return () => { aktiv = false; };
  }, [runde]);

  const ergebnis = useMemo(() => (regeln && testAktiv ? testeRegeln(regeln, test, new Date()) : null), [regeln, test, testAktiv]);
  const pfadVorschlaege = useMemo(() => [...new Set((regeln ?? []).map(r => r.ziel_pfad_vorlage))].sort(), [regeln]);
  const aktive = (regeln ?? []).filter(r => r.aktiv).length;

  const schalte = async (r: Regel, an: boolean) => {
    setRegeln(l => l?.map(x => (x.id === r.id ? { ...x, aktiv: an } : x)) ?? l);
    const { error } = await supabase.from('dokument_ablage_regeln').update({ aktiv: an, updated_at: new Date().toISOString() }).eq('id', r.id);
    if (error) { setRegeln(l => l?.map(x => (x.id === r.id ? { ...x, aktiv: !an } : x)) ?? l); zeigeToast(error.message, true); }
    else zeigeToast(`„${r.name}“ ${an ? 'aktiviert' : 'deaktiviert'}`);
  };
  const bestaetigeLoeschen = async () => {
    if (!loeschen) return;
    setLoescht(true);
    const { error } = await supabase.from('dokument_ablage_regeln').delete().eq('id', loeschen.id);
    setLoescht(false);
    if (error) { zeigeToast(fehlerText(error, 'Löschen fehlgeschlagen'), true); return; }
    zeigeToast(`Regel „${loeschen.name}“ gelöscht`);
    setLoeschen(null);
    setRunde(x => x + 1);
  };

  const testPanel = (
    <aside className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 22, padding: 16, gap: 10, ...(mobil ? {} : { position: 'sticky', top: 12 }) }} aria-label="Regel testen">
      <span className="flex items-center" style={{ gap: 8, fontSize: 15, fontWeight: 800 }}><Flask weight="fill" size={17} color="var(--acc)" />Regel testen</span>
      <label className="flex flex-col" style={{ gap: 6 }}>
        <span style={FELD_LABEL}>Betreff oder Text</span>
        <textarea value={test.text} onChange={e => setTest(t => ({ ...t, text: e.target.value }))} rows={2} placeholder="z. B. Rechnung 2026-118 – Alte Straße 17"
          style={{ ...EINGABE, height: 'auto', padding: '10px 14px', resize: 'vertical', fontWeight: 600 }} />
      </label>
      <TextFeld label="Dateiname" wert={test.dateiname} onChange={v => setTest(t => ({ ...t, dateiname: v }))} kompakt placeholder="Rechnung_2026-118.pdf" />
      <FeldRaster minBreite={130}>
        <AbsenderAuswahl label="Absender" wert={test.absender_typ} onChange={v => setTest(t => ({ ...t, absender_typ: v }))} alleLabel="Unbekannt" />
        <TextFeld label="Dateityp" wert={test.dateityp} onChange={v => setTest(t => ({ ...t, dateityp: v }))} kompakt placeholder="aus Dateiname" />
      </FeldRaster>
      <TextFeld label="Objektnummer (für die Vorschau)" wert={test.objektnummer} onChange={v => setTest(t => ({ ...t, objektnummer: v }))} kompakt placeholder="optional" />
      {testAktiv && ergebnis && (ergebnis.regel ? (
        <div className="flex flex-col" style={{ borderRadius: 16, padding: '12px 14px', gap: 6, background: 'var(--ok-soft)' }} role="status">
          <span className="flex items-center" style={{ gap: 6, fontSize: 13, fontWeight: 800, color: 'var(--ok)' }}><CheckCircle weight="fill" size={15} />{(ergebnis.regel as Regel).name}</span>
          <span style={{ fontSize: 12, color: 'var(--ink2)' }}>Zielordner</span>
          <code style={{ fontSize: 12, fontFamily: MONO, overflowWrap: 'anywhere' }}>{ergebnis.zielPfad}</code>
          <span style={{ fontSize: 12, color: 'var(--ink2)' }}>Dateiname</span>
          <code style={{ fontSize: 12, fontFamily: MONO, overflowWrap: 'anywhere' }}>{ergebnis.dateiname}</code>
        </div>
      ) : (
        <span role="status" style={{ fontSize: 12, color: 'var(--ink2)', borderRadius: 16, padding: '12px 14px', background: 'var(--fill2)' }}>Keine aktive Regel passt – ohne Treffer wählt der Mensch den Ordner.</span>
      ))}
      <span style={{ fontSize: 11, lineHeight: 1.5, color: 'var(--ink3)' }}>Es gewinnt die aktive Regel mit der kleinsten Priorität, bei der alle gesetzten Bedingungen passen. Leere Bedingungen gelten als erfüllt.</span>
    </aside>
  );

  return (
    <div className="flex flex-col min-w-0" style={{ gap: 14 }}>
      <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
        <div className="flex flex-col" style={{ gap: 2 }}>
          <h2 className="m-0" style={{ fontSize: 22, fontWeight: 800 }}>Ablage-Regeln</h2>
          {regeln && <span className="immo-zahl" style={{ fontSize: 13, color: 'var(--ink3)' }}>{aktive} von {regeln.length} Regeln aktiv · prüfen E-Mail und Dokumenttext</span>}
        </div>
        <ImmoButton art="akzent" groesse={44} icon={Plus} onClick={() => setDialog({ regel: null })}>Neue Regel</ImmoButton>
      </div>

      {fehler ? (
        <div role="alert" className="immo-glas flex items-center flex-wrap" style={{ borderRadius: 22, padding: 16, gap: 12 }}>
          <WarningCircle weight="fill" size={22} color="var(--bad)" /><span className="flex-1" style={{ fontSize: 13 }}>{fehler}</span>
          <ImmoButton icon={ArrowClockwise} onClick={() => setRunde(r => r + 1)}>Erneut versuchen</ImmoButton>
        </div>
      ) : !regeln ? (
        <div className="animate-pulse" style={{ height: 300, borderRadius: 26, background: 'var(--fill2)' }} aria-busy="true" />
      ) : (
        <div className="grid items-start" style={{ gridTemplateColumns: mobil ? 'minmax(0,1fr)' : 'minmax(0,1fr) minmax(280px, 340px)', gap: 14 }}>
          {mobil && testPanel}
          <div className="flex flex-col min-w-0" style={{ gap: 10 }}>
            {regeln.length === 0 && <LeerZustand icon={FlowArrow} titel="Noch keine Regeln" text="Ohne Regel schlägt die KI den Ordner vor oder der Mensch wählt ihn." />}
            {regeln.map(r => {
              const treffer = ergebnis?.regel?.id === r.id;
              return (
                <article key={r.id} className="immo-glas flex items-center flex-wrap min-w-0" style={{ borderRadius: 22, padding: 14, gap: 12, opacity: r.aktiv ? 1 : 0.6 }} aria-label={r.name}>
                  <span className="immo-zahl flex items-center justify-center shrink-0" style={{ width: 44, height: 44, borderRadius: 14, fontSize: 14, fontWeight: 800, background: treffer ? 'var(--ok)' : 'var(--fill2)', color: treffer ? '#fff' : 'var(--ink)' }}>{r.prioritaet}</span>
                  <div className="flex flex-col flex-1" style={{ gap: 6, minWidth: 200 }}>
                    <span className="flex items-center flex-wrap" style={{ gap: 8 }}>
                      <span style={{ fontSize: 15, fontWeight: 800 }}>{r.name}</span>
                      {treffer && <span className="flex items-center" style={{ height: 22, padding: '0 8px', gap: 4, borderRadius: 11, fontSize: 10, fontWeight: 800, background: 'var(--ok-soft)', color: 'var(--ok)' }}><CheckCircle weight="fill" size={11} />Treffer</span>}
                    </span>
                    <span className="flex flex-wrap" style={{ gap: 5 }}>
                      {(r.stichwoerter ?? []).length > 0 && <Kriterium icon={TextAa}>{(r.stichwoerter ?? []).join(', ')}</Kriterium>}
                      {r.absender_typ && <Kriterium icon={User}>{absenderTypLabel(r.absender_typ)}</Kriterium>}
                      {(r.dateityp ?? []).length > 0 && <Kriterium icon={File}>{(r.dateityp ?? []).map(d => d.toUpperCase()).join(', ')}</Kriterium>}
                      {!(r.stichwoerter ?? []).length && !r.absender_typ && !(r.dateityp ?? []).length && <Kriterium icon={TextAa}>ohne Bedingung – passt immer</Kriterium>}
                    </span>
                    <span className="flex items-center flex-wrap" style={{ gap: 6, fontSize: 12, color: 'var(--ink2)' }}>
                      <ArrowRight weight="bold" size={12} />
                      <code style={{ fontFamily: MONO, overflowWrap: 'anywhere' }}>{r.ziel_pfad_vorlage}</code>
                      <span style={{ height: 22, padding: '0 8px', borderRadius: 11, display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink)' }}>{r.dokument_wort}</span>
                    </span>
                  </div>
                  <div className="flex items-center shrink-0" style={{ gap: 6 }}>
                    <span className="flex items-center justify-center cursor-pointer" style={{ minWidth: 60, height: 44 }} onClick={e => { if (e.target === e.currentTarget) void schalte(r, !r.aktiv); }}><IosSwitch an={r.aktiv} onChange={an => void schalte(r, an)} label={`Regel „${r.name}“ aktiv`} /></span>
                    <button type="button" aria-label={`Regel „${r.name}“ bearbeiten`} onClick={() => setDialog({ regel: r })} className="immo-hover-flaeche flex items-center justify-center cursor-pointer"
                      style={{ width: 44, height: 44, borderRadius: 14, border: 'none', background: 'var(--fill2)', color: 'var(--ink)' }}><PencilSimple weight="bold" size={15} /></button>
                    <button type="button" aria-label={`Regel „${r.name}“ löschen`} onClick={() => setLoeschen(r)} className="immo-hover-flaeche flex items-center justify-center cursor-pointer"
                      style={{ width: 44, height: 44, borderRadius: 14, border: 'none', background: 'var(--fill2)', color: 'var(--bad)' }}><Trash weight="bold" size={15} /></button>
                  </div>
                </article>
              );
            })}
          </div>
          {!mobil && testPanel}
        </div>
      )}

      {dialog && <RegelDialog start={dialog.regel} pfadVorschlaege={pfadVorschlaege} onSchliessen={() => setDialog(null)} onGespeichert={t => { setDialog(null); zeigeToast(t); setRunde(r => r + 1); }} />}
      {loeschen && (
        <BestaetigenDialog titel="Regel löschen?" text="Neue Anhänge werden danach ohne diese Regel eingeordnet. Bereits erstellte Vorschläge bleiben." detail={<strong>{loeschen.name}</strong>}
          bestaetigenLabel="Löschen" laeuft={loescht} onBestaetigen={() => void bestaetigeLoeschen()} onAbbrechen={() => setLoeschen(null)} />
      )}
      <SchwebenderToast toast={toast} mobil={mobil} />
    </div>
  );
}
